// @flow
import * as React from 'react';
import useSheet from 'app/jss';
import classNames from 'classnames';
import { connect } from 'react-redux';
import * as ChatActions from 'app/actions/chatActions';
import * as SportCategoriesActions from 'app/actions/sportCategoriesActions';
import chatChannelsSelector from 'app/selectors/chat/chatChannelsSelector';
import t from 'app/utils/translate';
import Icon from 'app/components/commons/Icon';
import ChannelItem from 'app/components/chat/ChannelItem';
import Responsive from 'app/components/responsive/Responsive';
import Rows from '../layout/Rows';

type Props = {
  classes: Object,
  channels?: Array<ChatChannel>,
  currentChannel?: ChatChannel,
  feedbackUri?: string,
  setChannel: (channel: ChatChannel) => void,
  loadSportCategories: () => void,
  onHeaderClick?: () => void,
  onCloseClick?: () => void,
  children?: any,
  hasCategoriesInitalised: boolean
};

const defaultProps = {
  currentChannel: undefined,
  channels: [],
  feedbackUri: undefined,
  onCloseClick: undefined,
  children: undefined,
  onHeaderClick: undefined
};

function Chat(props: Props) {
  const [isChannelsOpen, setIsChannelsOpen] = React.useState(false);

  const {
    hasCategoriesInitalised,
    loadSportCategories,
    setChannel,
    classes,
    channels = [],
    currentChannel,
    feedbackUri,
    onHeaderClick,
    children,
    onCloseClick
  } = props;

  const handleLoadSportCategories = React.useCallback(loadSportCategories, []);

  React.useEffect(() => {
    // sport categories dictates our channels
    // if we haven't loaded sport categories yet from another source, load them
    if (!hasCategoriesInitalised) {
      handleLoadSportCategories();
    }
  }, [hasCategoriesInitalised, handleLoadSportCategories]);

  const toggleChannels = () => {
    setIsChannelsOpen(!isChannelsOpen);
  };

  const onSetChannel = channelId => {
    setChannel(channelId);
    toggleChannels();
  };

  return (
    <div className={classes.chatContainer}>
      <div
        role="button"
        className={classes.chatHeader}
        tabIndex="-1"
        onClick={onHeaderClick}
        onKeyPress={onHeaderClick}
      >
        <div className={classes.greenDot} />
        <span className={classes.wrapperText}>
          {(currentChannel ? `${currentChannel.name} ` : '') + t('Chat')}
        </span>
        <div className={classes.alignRight}>
          {channels.length > 0 && (
            <button
              type="button"
              className={classes.iconButtonHitbox}
              onClick={e => {
                e.stopPropagation();
                toggleChannels();
              }}
            >
              <Icon
                className={classes.currentChannelIcon}
                type={currentChannel ? currentChannel.icon : 'icon-sport-all'}
              />
              <Icon
                className={classNames(classes.channelIndicator, isChannelsOpen ? 'open' : 'closed')}
                type="icon-right-arrow"
              />
            </button>
          )}
          <Responsive desktop>
            <div className={classes.iconButtonHitbox}>
              <Icon className={classes.openCloseIcon} type="icon-minus" />
            </div>
          </Responsive>
          <Responsive mobile>
            <button type="button" className={classes.iconButtonHitbox} onClick={onCloseClick}>
              <Icon className={classes.openCloseIcon} type="icon-cross" />
            </button>
          </Responsive>
        </div>
      </div>
      <div className={classes.channelsContainer}>
        {channels.length > 0 && isChannelsOpen && (
          <Rows mobile={3} desktop={3}>
            {channels.map(channel => (
              <ChannelItem
                key={channel.id}
                channel={channel}
                onChannelClick={onSetChannel}
                isActive={channel.id === (currentChannel && currentChannel.id)}
              />
            ))}
          </Rows>
        )}
      </div>
      <div className={classes.chatFrame}>
        {!currentChannel && (
          <div className={classes.noChannel} key="noChannel">
            <Icon className={classes.noChannelIcon} type="icon-chat" />
            <br />
            {!isChannelsOpen && (
              <Icon className={classes.noChannelArrow} type="icon-casual-arrow" />
            )}
            {t("You're not chatting with anyone!")}
            <br />
            {t('Select a chat channel from above.')}
          </div>
        )}
        {currentChannel && (
          <React.Fragment>
            {currentChannel.id === 'feedback' && feedbackUri ? (
              <div className={classes.feedback} key="feedback">
                Here&quot;s your chance to help shape the future of&nbsp;Draftstars.
                <p>
                  We are using a 3rd party application to track feature requests and bugs. You can
                  vote and comment on what we should work on next.
                </p>
                <p>
                  Important: You will need to signup to contribute and if you don’t want your “real
                  name” to be public, please use an alias or your Draftstars username as your
                  “Full&nbsp;Name”.
                </p>
                <p>
                  Visit{' '}
                  <a href={feedbackUri} target="_blank" rel="noopener noreferrer">
                    https://draftstars.canny.io/
                  </a>{' '}
                  to get started.
                </p>
              </div>
            ) : (
              children
            )}
          </React.Fragment>
        )}
      </div>
    </div>
  );
}

Chat.defaultProps = defaultProps;

function applyStyles(theme: Theme) {
  return {
    chatContainer: {
      backgroundColor: 'white',
      borderTopLeftRadius: 8,
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1
    },
    chatFrame: {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative'
    },
    chatHeader: {
      display: 'flex',
      fontWeight: theme.fontWeight.demiBold,
      fontSize: 13,
      alignItems: 'center',
      textTransform: 'uppercase',
      color: theme.color.basis1,
      padding: [0, 20],
      flexShrink: 0,
      height: 40,
      cursor: 'pointer',
      [theme.mediaQuery.touch]: {
        height: 45,
        fontSize: 16
      }
    },
    noChannel: {
      color: theme.color.basis3,
      fontSize: 18,
      maxWidth: '80%',
      lineHeight: '30px',
      textAlign: 'center',
      fontWeight: 100
    },
    feedback: {
      color: theme.color.basis3,
      fontSize: theme.fontSize.small,
      maxWidth: '80%',
      lineHeight: '30px',
      textAlign: 'center',
      fontWeight: theme.fontWeight.normal,
      '& > p > a': {
        color: theme.color.primary
      }
    },
    noChannelIcon: {
      color: theme.color.basis5,
      fontSize: 60,
      marginBottom: 15
    },
    noChannelArrow: {
      position: 'absolute',
      top: 0,
      right: 50,
      fontSize: 50,
      [theme.mediaQuery.touch]: {
        right: 57
      }
    },
    channelsContainer: {
      borderBottom: [2, 'solid', theme.color.basis6]
    },
    channels: {
      backgroundColor: theme.color.basis6,
      padding: 10
    },
    wrapperText: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    wrapperIcon: {
      color: theme.color.basis5,
      fontSize: 30
    },
    greenDot: {
      backgroundColor: theme.color.success,
      height: 7,
      width: 7,
      borderRadius: '100%',
      marginRight: 10,
      '&.floating': {
        position: 'absolute',
        top: 5,
        left: 5
      }
    },
    alignRight: {
      marginLeft: 'auto',
      display: 'flex',
      alignItems: 'center',
      height: 40,
      marginRight: -10,
      [theme.mediaQuery.touch]: {
        marginRight: -20,
        height: 45
      }
    },
    openCloseIcon: {
      fontSize: 16,
      color: theme.color.basis5
    },
    iconButtonHitbox: {
      height: '100%',
      width: 40,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0,
      [theme.mediaQuery.touch]: {
        width: 50
      }
    },
    currentChannelIcon: {
      fontSize: 18,
      marginRight: 5,
      [theme.mediaQuery.touch]: {
        fontSize: 20
      }
    },
    channelIndicator: {
      transition: 'transform 0.3s ease-in-out',
      transform: 'rotate(0deg)',
      fontSize: 8,
      '&.open': {
        transform: 'rotate(450deg)'
      }
    }
  };
}

const actions = {
  setChannel: ChatActions.setChannel,
  loadSportCategories: SportCategoriesActions.getSportCategories
};

function mapStateToProps(state: GlobalState) {
  const channels = chatChannelsSelector(state);
  return {
    channels,
    currentChannel: channels.find(channel => channel.id === state.chat.channelId),
    hasCategoriesInitalised: state.sportCategories.hasInitialised,
    feedbackUri: state.settings.feedbackUri
  };
}

export default connect(
  mapStateToProps,
  actions
)(useSheet(applyStyles)(Chat));
